export const normalizeAnswer = (answer) => {
  if (typeof answer !== 'string') {
    return '';
  }

  return answer
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
};

const getDateInTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const values = parts.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  return new Date(Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  ));
};

const getDateTimePartsInTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  return parts.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = Number(part.value);
    }
    return acc;
  }, {
    year: 0,
    month: 0,
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
  });
};

export const getUTCDateKey = (date = new Date()) => {
  const parisDate = getDateInTimeZone(date, 'Europe/Paris');
  const year = parisDate.getUTCFullYear();
  const month = String(parisDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parisDate.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getParisDateKey = (date = new Date(), shift = 0) => {
  const parisDate = getDateInTimeZone(date, 'Europe/Paris');
  const shifted = new Date(Date.UTC(
    parisDate.getUTCFullYear(),
    parisDate.getUTCMonth(),
    parisDate.getUTCDate() + shift,
    0,
    0,
    0
  ));
  return getUTCDateKey(shifted);
};

export const getUTCDaysSinceEpoch = (dateKey, epoch = '2026-05-15') => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const [eYear, eMonth, eDay] = epoch.split('-').map(Number);
  const date = Date.UTC(year, month - 1, day);
  const start = Date.UTC(eYear, eMonth - 1, eDay);
  return Math.floor((date - start) / 86400000);
};

export const getDayNumber = (dateKey) => getUTCDaysSinceEpoch(dateKey) + 1;

export const seedFromString = (value) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 2147483647;
  }
  return hash || 1;
};

export const seededRandom = (seed) => {
  let state = seed % 2147483647;
  if (state <= 0) {
    state += 2147483646;
  }

  return () => {
    state = (state * 48271) % 2147483647;
    return state / 2147483647;
  };
};

export const getRevealOrder = (seed, size) => {
  const random = seededRandom(seed);
  const items = Array.from({ length: size }, (_, index) => index);
  for (let i = size - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

export const getDailyFlagIndex = (countries, dateKey) => {
  if (!countries || countries.length === 0) {
    return 0;
  }
  const sorted = [...countries].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  const page = getUTCDaysSinceEpoch(dateKey);
  return ((page % sorted.length) + sorted.length) % sorted.length;
};

export const getDailyFlag = (countries, dateKey) => {
  const index = getDailyFlagIndex(countries, dateKey);
  const sorted = [...countries].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  return sorted[index] || null;
};

export const revealLevels = [0.15, 0.30, 0.50, 0.70, 1];

export const getRevealCount = (stage, total) => {
  const level = revealLevels[Math.min(Math.max(stage, 0), revealLevels.length - 1)];
  return Math.max(1, Math.min(total, Math.round(total * level)));
};

export const getDailyRevealMask = (dateKey, stage, rows = 5, cols = 5) => {
  const total = rows * cols;
  const seed = seedFromString(`${dateKey}-${stage}`);
  const order = getRevealOrder(seed, total);
  const visibleCount = getRevealCount(stage, total);
  return new Set(order.slice(0, visibleCount));
};

const getTimeZoneOffsetMinutes = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const offsetPart = parts.find((part) => part.type === 'timeZoneName');
  const offsetString = offsetPart?.value || 'GMT+0';
  const match = offsetString.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);

  if (!match) {
    return 0;
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = Number(match[2] || 0);
  const minutes = Number(match[3] || 0);

  return sign * (hours * 60 + minutes);
};

const getUtcDateFromZonedParts = ({ year, month, day, hour = 0, minute = 0, second = 0 }, timeZone) => {
  const candidateUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let utcTime = candidateUtc;

  for (let i = 0; i < 3; i += 1) {
    const adjusted = new Date(utcTime);
    const offset = getTimeZoneOffsetMinutes(adjusted, timeZone) * 60000;
    const nextUtcTime = candidateUtc - offset;
    if (nextUtcTime === utcTime) {
      break;
    }
    utcTime = nextUtcTime;
  }

  return new Date(utcTime);
};

export const getNextParisReset = () => {
  const now = new Date();
  const parisParts = getDateTimePartsInTimeZone(now, 'Europe/Paris');

  return getUtcDateFromZonedParts({
    year: parisParts.year,
    month: parisParts.month,
    day: parisParts.day + 1,
    hour: 0,
    minute: 0,
    second: 0,
  }, 'Europe/Paris');
};

export const getTimeUntil = (target) => {
  const remaining = Math.max(target.getTime() - Date.now(), 0);
  const hours = String(Math.floor(remaining / 3600000)).padStart(2, '0');
  const minutes = String(Math.floor((remaining % 3600000) / 60000)).padStart(2, '0');
  const seconds = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};
